import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../i18n";
import { servicesBySlug, services, pick } from "../data/services";
import { Seo } from "../components/site/Seo";
import { MaskLine, FadeUp } from "../components/site/Reveal";
import { CTA } from "../components/site/CTA";
import * as Icons from "lucide-react";
import { ArrowUpRight, Check, ArrowLeft } from "lucide-react";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { lang } = useLang();
  const s = servicesBySlug[slug];

  if (!s) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-[#050505] px-6 text-center">
        <h1 className="font-display font-bold text-4xl">Service introuvable</h1>
        <Link to="/services" className="px-6 py-3 rounded-full border border-white/20 hover:border-[var(--primary)] font-mono-p text-sm transition-colors">← Nos services</Link>
      </div>
    );
  }

  const c = s[lang];
  const Icon = Icons[s.icon] || Icons.Plane;
  const related = services.filter((x) => x.slug !== slug).slice(0, 3);
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Service",
    serviceType: c.name, provider: { "@type": "Organization", name: "Fixedrone" },
    areaServed: ["Luxembourg", "France", "Belgique", "Allemagne"],
    description: c.metaDesc,
  };
  const lbl = { fr: { back: "Services", deliver: "Ce que vous obtenez", cases: "Cas d'usage", benefits: "Vos avantages", gallery: "Aperçu", related: "Autres services", learn: "Découvrir" }, en: { back: "Services", deliver: "What you get", cases: "Use cases", benefits: "Your benefits", gallery: "Preview", related: "Other services", learn: "Discover" } }[lang];

  return (
    <>
      <Seo title={c.metaTitle} description={c.metaDesc} keywords={`${c.name}, drone Luxembourg, ${s.slug.replace(/-/g, ", ")}, France, Belgique, Allemagne`} path={`/services/${s.slug}`} image={s.hero} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative pt-40 pb-16 md:pb-24 overflow-hidden bg-[#050505]">
        <div className="absolute inset-0 z-0">
          <img src={s.hero} alt={c.name} className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/50" />
        </div>
        <div className="relative z-10 w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <FadeUp>
            <Link to="/services" className="inline-flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest text-white/50 hover:text-primary-c transition-colors mb-8"><ArrowLeft size={14} /> {lbl.back}</Link>
          </FadeUp>
          <div className="flex items-center gap-3 mb-5">
            <span className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-primary-c"><Icon size={20} /></span>
            <span className="font-mono-p text-xs uppercase tracking-[0.25em] text-primary-c">Fixedrone · {c.name}</span>
          </div>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.9] max-w-4xl"><MaskLine load>{c.name}</MaskLine></h1>
          <FadeUp delay={0.1}><p className="text-white/70 mt-6 max-w-2xl text-lg leading-relaxed">{c.intro}</p></FadeUp>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-20 md:py-28 bg-[#070708]">
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <FadeUp><span className="font-mono-p text-xs uppercase tracking-[0.25em] text-primary-c">{lbl.deliver}</span></FadeUp>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {c.deliverables.map((d, i) => (
              <FadeUp key={i} delay={(i % 2) * 0.05}>
                <div className="bg-[#0a0a0c] p-8 h-full hover:bg-[#0f0f12] transition-colors duration-500">
                  <div className="flex items-start gap-4">
                    <span className="shrink-0 mt-1 w-6 h-6 rounded-full bg-[var(--primary)]/15 text-primary-c flex items-center justify-center"><Check size={14} /></span>
                    <div>
                      <h3 className="font-display font-semibold text-xl">{d.t}</h3>
                      <p className="text-white/55 text-sm mt-2 leading-relaxed">{d.d}</p>
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases + benefits */}
      <section className="py-20 md:py-28 bg-[#050505]">
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <FadeUp><span className="font-mono-p text-xs uppercase tracking-[0.25em] text-primary-c">{lbl.cases}</span></FadeUp>
            <div className="mt-8 grid sm:grid-cols-2 gap-8">
              {s.groups.map((grp, i) => (
                <FadeUp key={i} delay={i * 0.05}>
                  <div>
                    <h3 className="font-display font-semibold text-lg mb-3 flex items-center gap-2"><span className="text-primary-c font-mono-p text-sm">→</span>{pick(grp.t, lang)}</h3>
                    <ul className="space-y-2">
                      {grp.items.map((it, j) => (<li key={j} className="text-white/55 text-sm leading-relaxed pl-4 border-l border-white/10">{pick(it, lang)}</li>))}
                    </ul>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <FadeUp><span className="font-mono-p text-xs uppercase tracking-[0.25em] text-primary-c">{lbl.benefits}</span></FadeUp>
            <ul className="mt-8 space-y-4">
              {c.benefits.map((b, i) => (
                <FadeUp key={i} delay={i * 0.05}>
                  <li className="flex items-start gap-3 border border-white/10 rounded-xl p-5 bg-[#0a0a0c]">
                    <span className="shrink-0 mt-0.5 w-7 h-7 rounded-full bg-[var(--primary)] text-black flex items-center justify-center"><Check size={15} /></span>
                    <span className="text-white/80 leading-relaxed">{b}</span>
                  </li>
                </FadeUp>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="pb-24 md:pb-32 bg-[#050505]">
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <FadeUp><span className="font-mono-p text-xs uppercase tracking-[0.25em] text-primary-c">{lbl.gallery}</span></FadeUp>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {s.gallery.map((img, i) => (
              <FadeUp key={i} delay={i * 0.06}>
                <div className={`overflow-hidden clip-frame border border-white/10 ${i === 0 ? "md:col-span-2 aspect-[16/9]" : "aspect-[4/3]"}`}>
                  <img src={img} alt={`${c.name} — Fixedrone ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.2s]" />
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-20 md:py-24 bg-[#070708] border-t border-white/10">
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-10">{lbl.related}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link key={r.slug} to={`/services/${r.slug}`} className="group relative overflow-hidden clip-frame border border-white/10 aspect-[4/3]" data-testid={`related-${r.slug}`}>
                <img src={r.hero} alt={r[lang].name} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-[1s]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="relative p-6 h-full flex flex-col justify-end">
                  <h3 className="font-display font-semibold text-xl">{r[lang].name}</h3>
                  <span className="mt-2 inline-flex items-center gap-1.5 font-mono-p text-xs uppercase tracking-widest text-primary-c">{lbl.learn} <ArrowUpRight size={13} className="group-hover:rotate-45 transition-transform duration-300" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CTA />
    </>
  );
}
